import { getFileExtension } from "@/modern/utils";
import { isMediaFile } from "@/other/utils/utils.jsx";

export type EntityFileUploadCategory = "media" | "documents";

const DOCUMENT_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
]);

const isMediaMime = (mimeType: string) =>
  mimeType.startsWith("image/") || mimeType.startsWith("video/") || mimeType.startsWith("audio/");

export const ENTITY_FILE_UPLOAD_CONFIG = {
  media: {
    accept: "image/*,video/*,audio/*",
    rejectionMessage: "Only image, video, and audio files are allowed.",
    isAllowedMime: isMediaMime,
  },
  documents: {
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv",
    rejectionMessage: "Only PDF, Word, Excel, PowerPoint, text, and CSV files are allowed.",
    isAllowedMime: (mimeType: string) => DOCUMENT_MIME_TYPES.has(mimeType),
  },
} as const satisfies Record<
  EntityFileUploadCategory,
  { accept: string; rejectionMessage: string; isAllowedMime: (mimeType: string) => boolean }
>;

/** Extensions from the documents `accept` list (e.g. ".pdf,.doc" → {"pdf","doc"}). */
const DOCUMENT_EXTENSIONS = new Set(
  ENTITY_FILE_UPLOAD_CONFIG.documents.accept
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.startsWith("."))
    .map((part) => part.slice(1).toLowerCase()),
);

/** Classify a file for upload when MIME may be empty (common on some OS drag sources). */
export function classifyEntityUploadFile(file: File): EntityFileUploadCategory | null {
  const mime = file.type || "";
  if (mime && ENTITY_FILE_UPLOAD_CONFIG.media.isAllowedMime(mime)) return "media";
  if (mime && ENTITY_FILE_UPLOAD_CONFIG.documents.isAllowedMime(mime)) return "documents";

  // Reuse existing extension helpers / accept list when browser leaves type empty
  if (isMediaFile(file)) return "media";
  if (DOCUMENT_EXTENSIONS.has(getFileExtension(file.name))) return "documents";
  return null;
}

export function filterFilesByAllowedMime(files: FileList | File[], category: EntityFileUploadCategory) {
  const { isAllowedMime } = ENTITY_FILE_UPLOAD_CONFIG[category];
  const selected = Array.from(files);
  const accepted = selected.filter((file) => {
    if (isAllowedMime(file.type)) return true;
    // Fallback when browser leaves type empty
    return classifyEntityUploadFile(file) === category;
  });
  return { accepted, rejectedCount: selected.length - accepted.length };
}

/**
 * Partition a multi-file schedule drop into media + documents.
 * Unlike widget drops, schedule bars accept both categories in one gesture.
 */
export function partitionFilesForScheduleDrop(files: FileList | File[]) {
  const media: File[] = [];
  const documents: File[] = [];
  const rejected: File[] = [];

  for (const file of Array.from(files)) {
    const category = classifyEntityUploadFile(file);
    if (category === "media") media.push(file);
    else if (category === "documents") documents.push(file);
    else rejected.push(file);
  }

  return { media, documents, rejected, accepted: [...media, ...documents] };
}
